// frontend/src/components/common/Pagination.jsx
// COMPONENTE PAGINADOR REUTILIZABLE UNIFICADO CON ListaUnificada
import React from 'react';
import styled from 'styled-components';

const PaginatorContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-top: none;
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md};
  min-height: 36px;
  flex-shrink: 0;
  z-index: 10;
`;

const PaginationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PerPageSelect = styled.select`
  height: 28px;
  padding: 0 24px 0 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.8rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23999' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 7px center;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PageButton = styled.button`
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $active, theme }) => $active ? theme.colors.info : theme.colors.surface};
  color: ${({ $active, theme }) => $active ? theme.colors.onPrimary : theme.colors.txtPrincipal};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.8rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) => $active ? theme.colors.infoHover : theme.colors.surfaceHover};
  }

  &:disabled {
    opacity: 0.45;
  }
`;

const PageInfo = styled.span`
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: 0.8rem;
  margin-left: 8px;
`;

// COMPONENTE PAGINATION PRINCIPAL
const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 25,
  perPageOptions = [10, 25, 50, 100],
  onPageChange,
  onPerPageChange,
  showPerPageSelector = true,
  showTotalCounter = true,
  itemLabel = 'elementos',
  showPageInfo = true,
  compact = false,
}) => {

  if (!totalItems || totalItems <= 0) {
    return null;
  }

  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageRange = () => {
    const maxPages = 5;
    const halfRange = Math.floor(maxPages / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pages = getPageRange();
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange && onPageChange(page);
    }
  };

  const handlePerPageChange = (e) => {
    onPerPageChange && onPerPageChange(parseInt(e.target.value, 10));
  };

  return (
    <PaginatorContainer>
      <PaginationInfo>
        {showPerPageSelector && (
          <ItemsPerPageContainer>
            <span>Mostrar:</span>
            <PerPageSelect value={itemsPerPage} onChange={handlePerPageChange}>
              {perPageOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </PerPageSelect>
            <span>por página</span>
          </ItemsPerPageContainer>
        )}

        {showTotalCounter && (
          <span>
            Mostrando {startItem} a {endItem} de {totalItems} {itemLabel}
          </span>
        )}
      </PaginationInfo>

      {!compact && (
        <PaginationControls>
          <PageButton disabled={!hasPrevious} onClick={() => handlePageChange(1)}>««</PageButton>
          <PageButton disabled={!hasPrevious} onClick={() => handlePageChange(currentPage - 1)}>‹</PageButton>

          {pages.map(page => (
            <PageButton key={page} $active={page === currentPage} onClick={() => handlePageChange(page)}>
              {page}
            </PageButton>
          ))}

          <PageButton disabled={!hasNext} onClick={() => handlePageChange(currentPage + 1)}>›</PageButton>
          <PageButton disabled={!hasNext} onClick={() => handlePageChange(totalPages)}>»»</PageButton>

          {showPageInfo && <PageInfo>Página {currentPage} de {Math.max(1, totalPages)}</PageInfo>}
        </PaginationControls>
      )}
    </PaginatorContainer>
  );
};

export default Pagination;
